import { ChangeEvent, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { config } from "../../../../config";
import { tokens } from "../../../../locales/tokens";
import {
  useDeleteBadgeMutation,
  useGetBadgesQuery,
} from "../../../../store/badge/useCases";

type TabType = "ACTIVE" | "ARCHIVE";

export const useBadges = () => {
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const tabOptions = [
    { label: t(tokens.badge.tabs.active), value: "ACTIVE" },
    { label: t(tokens.badge.tabs.archive), value: "ARCHIVE" },
  ];

  const tabFromUrl = (searchParams.get("tab") as TabType) || "ACTIVE";
  const pageFromUrl = Number(searchParams.get("page")) || 1;

  const [tab, setTab] = useState<TabType>(tabFromUrl);
  const [filterBadges, setFilterBadges] = useState({
    perPage: config.badge.perPage,
    page: pageFromUrl,
    type: tabFromUrl,
  });

  const updateSearchParams = (params: { tab?: TabType; page?: number }) => {
    setSearchParams({
      tab: params.tab ?? tab,
      page: String(params.page ?? filterBadges.page),
    });
  };

  const handleTabsChange = (_: ChangeEvent<{}>, newTab: string): void => {
    const typedTab = newTab as TabType;

    setTab(typedTab);
    setFilterBadges((prev) => ({
      ...prev,
      type: typedTab,
      page: 1,
    }));

    updateSearchParams({ tab: typedTab, page: 1 });
    scrollTopList();
  };

  const handleChangePageBadge = (
    _: ChangeEvent<unknown>,
    page: number,
  ): void => {
    setFilterBadges((prev) => ({ ...prev, page }));
    updateSearchParams({ page });
    scrollTopList();
  };

  const {
    data: { rows: rowsBadges = [], count: countBadges = 0 } = {},
    isLoading: isLoadingGetBadges,
  } = useGetBadgesQuery(filterBadges);

  const [
    deleteBadge,
    { isLoading: isLoadingDeleteBadges, isSuccess: isSuccessDeleteBadges },
  ] = useDeleteBadgeMutation();

  useEffect(() => {
    if (isSuccessDeleteBadges) {
      tab === "ACTIVE"
        ? toast.success(t(tokens.badge.messages.delete))
        : toast.success(t(tokens.badge.messages.deleteHard));
    }
  }, [isSuccessDeleteBadges, tab, t]);

  const isArchive = tab === "ARCHIVE";

  const scrollTopList = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  };

  return {
    t,
    tab,
    tabOptions,
    handleTabsChange,
    isLoadingGetBadges,
    rowsBadges,
    isArchive,
    isLoadingDeleteBadges,
    deleteBadge,
    countBadges,
    filterBadges,
    handleChangePageBadge,
    scrollRef,
  };
};
